<template>
  <div
    v-if="!isGamestopStore && !showLegacyMetrics"
    :class="[$style.config, $style.wrap]"
    :style="data?.fields?.cssOverride as StyleValue"
    data-tier-benefits-v="0.12.0"
  >
    <div :class="[$style.inner, open && $style.open]">
      <div :class="[$style.header, missingData && $style.disablePointerEvents]">
        <div :class="$style.triggerWrap" @click="handleToggleDrawer">
          <div :class="$style.trigger">
            <BaseHeading size="h6" as="h2" :class="$style.heading">
              {{ data?.fields?.header }}
            </BaseHeading>
            <button v-if="!missingData" :class="$style.chevron">
              <IconChevronUp v-if="open" width="12" height="12" />
              <IconChevronDown v-else width="12" height="12" />
            </button>
          </div>
        </div>
        <div :class="$style.meta">
          <MarkedParser
            :md="computedSubheader"
            :class="['u-fs-p1', $style.metaCopy]"
          />
        </div>
      </div>
      <div :class="$style.contentWrap">
        <div
          :class="[$style.content, missingData && $style.missingData]"
          :inert="missingData"
          :aria-hidden="missingData ? 'true' : undefined"
        >
          <MarkedParser :md="computedCopy" :class="['u-fs-p1', $style.copy]" />
          <div :class="$style.tabs">
            <button
              v-for="tab in computedTabs"
              :key="tab.value"
              :class="[
                $style.tab,
                selectedTier === tab.value && $style.active,
                (tierOverride || tier) < tab.value && $style.locked,
              ]"
              @click="selectTab(tab.value)"
            >
              <IconLock
                v-if="(tierOverride || tier) < tab.value"
                width="16"
                height="16"
              />
              {{ tab.label }}
            </button>
          </div>
          <div
            v-if="selectedTier >= 0"
            :class="[
              $style.benefits,
              computedBenefits?.length === 4 && $style.onlyFour,
              (tierOverride || tier) < selectedTier && $style.locked,
            ]"
          >
            <div
              v-for="benefit in computedBenefits"
              :key="benefit?.sys?.id"
              :class="$style.benefit"
            >
              <header>
                <template v-if="benefit?.fields?.link">
                  <a
                    :href="benefit?.fields?.link"
                    target="_blank"
                    :class="$style.benefitTitle"
                    rel="noopener noreferrer"
                    @click.prevent="handleBenefitLinkClick(benefit)"
                  >
                    {{ benefit?.fields?.header }}
                    <IconExternalLink
                      width="16"
                      height="16"
                      :class="$style.benefitTitleIcon"
                    />
                  </a>
                </template>
                <span
                  v-else
                  :class="$style.benefitTitle"
                  v-html="benefit?.fields?.header"
                ></span>
              </header>
              <MarkedParser
                :md="benefit?.fields?.copy"
                :class="[$style.benefitCopy]"
              />
            </div>
          </div>
          <div v-else :class="$style.benefitGroups">
            <div
              v-for="benefitGroup in computedCurrentBenefits"
              :key="benefitGroup?.title"
              :class="$style.benefitGroup"
            >
              <div :class="$style.benefitTitle">{{ benefitGroup?.title }}</div>
              <ul :class="$style.benefitList">
                <li
                  v-for="benefit in benefitGroup?.benefits"
                  :key="benefit?.sys?.id"
                >
                  {{ benefit?.fields?.header }}
                </li>
              </ul>
            </div>
          </div>
          <MarkedParser
            :md="data?.fields?.footnote"
            :class="['u-fs-p1', $style.footnote]"
          />
        </div>
        <div v-if="missingData" :class="$style.missingDataError">
          <div :class="$style.error">
            {{ computedErrorText }}
            <CallToAction
              v-if="data.fields.errorCta"
              :cta="data.fields.errorCta"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TypeTierBenefitsWithoutUnresolvableLinksResponse } from '~/types/contentful'
import type { StyleValue } from 'vue'

import { addQuarters, startOfMonth } from 'date-fns'
import { formatLocalizedDate } from '~/utils/localizeDates'

const route = useRoute()
const { locale } = useI18n()
const { $visibility } = useNuxtApp()

const props = defineProps<{
  data: TypeTierBenefitsWithoutUnresolvableLinksResponse
}>()

const { localizedStrings } = useLocalizedStrings()
const userStore = useUserStore()
const { stores, roles, rolesRetrieved } = storeToRefs(userStore)

const currentStore = computed(
  () =>
    stores.value?.find((store) => store?.id === arrayAsString(route.query.s)) ||
    stores.value?.[0],
)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ufetch = $fetch as typeof $fetch<any>

const { data: profileMetricsData } = await useAsyncData(
  `profile-metrics-data-${route.params?.slug}-${locale.value}`,
  async () => {
    return await ufetch('/api/profile-metrics', {
      method: 'POST',
      body: { roles: roles?.value },
    })
  },
  {
    transform(input) {
      return {
        metrics: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    watch: [() => locale.value],
  },
)

const testProfileMetricsData = await useTestProfileMetrics()

const missingData = ref(
  !(
    currentStore.value &&
    Boolean(
      testProfileMetricsData.value?.metrics[currentStore.value.id]?.store_tier,
    )
  ),
)

const tier = ref(
  (currentStore.value &&
    testProfileMetricsData.value?.metrics[currentStore.value.id]?.store_tier
      ?.current?.value) ||
    0,
)

const tierOverride = ref<number | null | undefined>(
  currentStore.value &&
    (testProfileMetricsData.value?.metrics[currentStore.value.id]?.store_tier
      ?.override?.effective
      ? testProfileMetricsData.value?.metrics[currentStore.value.id]?.store_tier
          ?.override?.value
      : undefined),
)

const selectedTier = ref(tierOverride.value || tier.value)
const lastUpdated = ref(
  (currentStore.value &&
    formatRawDate(
      testProfileMetricsData.value?.metrics[currentStore.value.id]
        ?.last_updated_quarter_date,
    )) ||
    startOfMonth(new Date()).toISOString(),
)
const nextUpdate = ref(
  (currentStore.value &&
    formatRawDate(
      testProfileMetricsData.value?.metrics[currentStore.value.id]
        ?.next_updated_quarter_date,
    )) ||
    addQuarters(startOfMonth(new Date()), 1).toISOString(),
)
const open = ref(true)

const isGamestopStore = computed(() => checkIsGamestopStore(currentStore.value))

const { showLegacyMetrics } = useLegacyMetricsView()

const computedErrorText = computed(() => {
  const id = props.data.fields.errorText?.fields?.id || ''
  return localizedStrings.value[id] || ''
})

const computedSubheader = computed(() => {
  let subheader = props.data?.fields?.subheader

  if (subheader?.includes(`{{ previousEvaluationDate }}`)) {
    subheader = subheader.replace(
      `{{ previousEvaluationDate }}`,
      formatLocalizedDate(lastUpdated.value, 'MM/dd/yyyy', locale.value),
    )
  }

  if (subheader?.includes(`{{ nextEvaluationDate }}`)) {
    subheader = subheader.replace(
      `{{ nextEvaluationDate }}`,
      formatLocalizedDate(nextUpdate.value, 'MM/dd/yyyy', locale.value),
    )
  }

  return subheader
})

const tierHasBenefits = (tierValue: number) => {
  const key = `tier${tierValue}` as
    | 'tier0'
    | 'tier1'
    | 'tier2'
    | 'tier3'
    | 'tier4'
    | 'tier5'
    | 'tier6'
  const benefits = props.data?.fields?.[key]
  if (!Array.isArray(benefits)) return false
  return benefits.some((b) => $visibility(b?.fields?.visibility))
}

const computedTabs = computed(() => {
  return [
    { label: localizedStrings.value['tier0'], value: 0 },
    { label: localizedStrings.value['tier1'], value: 1 },
    { label: localizedStrings.value['tier2'], value: 2 },
    { label: localizedStrings.value['tier3'], value: 3 },
    { label: localizedStrings.value['tier4'], value: 4 },
    { label: localizedStrings.value['tier5'], value: 5 },
    { label: localizedStrings.value['tier6'], value: 6 },
    { label: localizedStrings.value['see-current-benefits'], value: -1 },
  ].filter((tab) => tab.value === -1 || tierHasBenefits(tab.value))
})

const computedBenefits = computed(() => {
  const key = `tier${selectedTier.value}` as
    | 'tier1'
    | 'tier1'
    | 'tier2'
    | 'tier3'
    | 'tier4'
    | 'tier5'
    | 'tier6'

  const benefits = props.data?.fields?.[key] || []
  const filtered = benefits.filter((b) => $visibility(b?.fields?.visibility))

  return filtered
})

const computedCurrentBenefits = computed(() => {
  const currentBenefits = []

  for (let i = 0; i <= (tierOverride.value || tier.value); i++) {
    const key = `tier${i}` as
      | 'tier1'
      | 'tier1'
      | 'tier2'
      | 'tier3'
      | 'tier4'
      | 'tier5'
      | 'tier6'

    const benefits = props.data?.fields?.[key] || []
    const filtered = benefits.filter((b) => $visibility(b?.fields?.visibility))

    if (filtered?.length) {
      currentBenefits.push({
        title: localizedStrings.value[`${key}-benefits`],
        benefits: filtered,
      })
    }
  }

  return currentBenefits
})

interface Store {
  id: string
  isPremium?: boolean
  // dynamic keys for stats (e.g. store[stat.id] -> number)
  [key: string]: unknown
}

const computedStoreMetrics = computed(() => {
  const stores = profileMetricsData.value?.metrics

  if (!stores || !rolesRetrieved.value) {
    return {}
  }

  return stores.reduce((acc: { [key: string]: unknown }, curr: Store) => {
    // Only get averageWeeklyPromoPacks since that's the only metric used for now
    const { id, averageWeeklyPromoPacks } = curr
    acc[id] = { averageWeeklyPromoPacks }
    return acc
  }, {})
})

const computedCopy = computed(() => {
  const copy = props.data?.fields?.copy

  if (copy?.includes(`{{ promoPackCount }}`)) {
    const promoPackCount =
      currentStore.value &&
      computedStoreMetrics.value?.[currentStore.value?.id]
        ?.averageWeeklyPromoPacks

    return copy.replace(`{{ promoPackCount }}`, promoPackCount || '4')
  }

  return copy
})

const handleToggleDrawer = () => {
  open.value = !open.value
}

const selectTab = (tier: number) => {
  selectedTier.value = tier

  const eventName =
    tier == -1 ? 'see_current_benefits_click' : 'benefits_tier_selected'

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: eventName,
    eventParams: {
      ...defaultEventParams.value,
    },
  })
}

const handleBenefitLinkClick = (
  benefit: (typeof computedBenefits.value)[number],
) => {
  const link = benefit?.fields?.link

  if (!link) return

  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'benefit_click',
    eventParams: {
      ...defaultEventParams.value,
      benefit_name: benefit?.fields?.header || '',
      benefit_program: benefit?.fields?.entryTitle || '',
      benefit_link_type: 'text_link',
      link_url: link,
      outbound: link.includes('wpn.wizards.com') ? false : true,
    },
  })

  setTimeout(() => {
    window.open(link, '_blank', 'noopener,noreferrer')
  }, 100)
}

const defaultEventParams = computed(() => {
  let tierName = `Tier ${selectedTier.value}`

  if (selectedTier.value === -1) {
    tierName = `See Your Current Benefits`
  }

  if (selectedTier.value === 0) {
    tierName = 'Entry Tier'
  }

  return {
    retailer_current_tier: String(tier.value),
    store: currentStore.value?.name + ' (' + currentStore.value?.id + ')',
    benefits_tier_name: tierName,
    benefits_tier_index: String(selectedTier.value),
  }
})

watch(
  () => [route.query.s],
  async ([s]) => {
    if (arrayAsString(s)) {
      const storeTierData =
        testProfileMetricsData.value?.metrics?.[arrayAsString(s) as string]
          ?.store_tier

      tier.value = storeTierData?.current?.value || 0
      tierOverride.value = storeTierData?.override?.effective
        ? storeTierData?.override?.value
        : undefined
      missingData.value = !storeTierData
    } else if (stores.value?.[0]?.id) {
      const storeTierData =
        testProfileMetricsData.value?.metrics?.[stores.value[0].id]?.store_tier

      tier.value = storeTierData?.current?.value || 0
      tierOverride.value = storeTierData?.override?.effective
        ? storeTierData?.override?.value
        : undefined
      missingData.value = !storeTierData
    } else {
      tier.value = 0
      tierOverride.value = undefined
      missingData.value = true
    }
    selectedTier.value = tierOverride.value || tier.value
  },
  { immediate: true },
)
</script>

<style lang="scss" module>
.config {
  --tb-padding-top: 24px;
  --tb-padding-bottom: 24px;
}

.wrap {
  padding: var(--tb-padding-top) 0 var(--tb-padding-bottom);

  @include media('sm') {
    padding: var(--tb-padding-top) 24px var(--tb-padding-bottom);
  }
}

.inner {
  display: flex;
  flex-direction: column;
  background: var(--white);
  border-radius: var(--border-radius);
  box-shadow: 0 0 4px 0 rgba(#000, 0.25);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding-right: 24px;
}

.triggerWrap {
  display: flex;
  flex-grow: 1;
  padding: 16px 24px;

  &:hover {
    cursor: pointer;
  }
}

.trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
}

.chevron {
  all: unset;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  background: var(--purple);
  color: var(--white);
  border-radius: 9999px;

  .triggerWrap:hover & {
    background: var(--purple-light);
  }
}

.meta {
  display: flex;
  gap: 4px;
  padding: 10px 0;
  text-align: right;
  font-size: 14px;

  @include media('md') {
    text-align: left;
  }
}

.metaCopy {
  display: flex;
  flex-direction: column;

  a {
    font-weight: 700;
    text-decoration: underline;
    color: var(--purple);

    &:hover {
      color: var(--purple-light);
    }
  }

  @include media('md') {
    flex-direction: row;
    gap: 4px;
  }
}

.tooltip {
  display: flex;
  width: min-content;
  height: min-content;
}

.info {
  min-width: 18px;
  min-height: 18px;
  color: #828282;

  &:hover {
    cursor: pointer;
  }

  @include media('sm') {
    min-width: 14px;
    min-height: 14px;
  }
}

.contentWrap {
  position: relative;
}

.content {
  display: none;
  flex-direction: column;
  gap: 16px;
  padding: 16px 24px;
  padding-top: 0;

  .open & {
    display: flex;
  }
}

.copy {
  font-size: to-rem(17px);
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  @include media('xs') {
    column-gap: 24px;
    row-gap: 10px;
  }
}

.tab {
  all: unset;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px;
  border-radius: var(--border-radius);
  border: 1px solid var(--purple);
  color: var(--purple);
  font-weight: 700;
  font-size: to-rem(14px);

  &:hover {
    background: var(--purple);
    color: var(--white);
    cursor: pointer;
  }

  &.active {
    background: var(--purple);
    color: var(--white);

    &:hover {
      background: var(--purple-light);
    }
  }

  &.locked {
    border-color: #828282;
    background: transparent;
    color: #828282;

    &.active {
      border-color: var(--gray-2);
      background: var(--gray-4);
      color: var(--gray-7);
    }

    &:hover {
      border-color: var(--gray-2);
      background: var(--gray-2);
      color: var(--gray-7);
    }
  }
}

.benefits,
.benefitGroups {
  display: flex;
  flex-wrap: wrap;
  column-gap: 24px;
  row-gap: 16px;
}

.benefit {
  border: 1px solid #e0e0e0;
  border-radius: var(--border-radius);
  padding: 16px;
  flex: 0 0 100%;

  @include media('sm') {
    flex: 1 0 calc(50% - 24px);
  }

  @include media('md') {
    flex: 1 0 calc(33.33% - 24px);

    .onlyFour & {
      flex: 1 0 calc(50% - 24px);
    }
  }
}

.benefitGroups {
  column-gap: 72px;
  row-gap: 32px;
  border: 1px solid #e0e0e0;
  border-radius: var(--border-radius);
  padding: 16px;
}

.benefitList {
  list-style: none;
  padding: 0;
}

.benefitTitle,
.benefitCopy {
  font-size: to-rem(16px);
}

.benefitTitle {
  display: block;
  max-width: max-content;
  margin-bottom: 4px;
  font-weight: 700;
  color: var(--purple);

  .locked & {
    color: #828282;
  }

  &:is(a):hover {
    filter: brightness(1.5);
  }
}

.benefitTitleIcon {
  display: inline;
  vertical-align: -0.125em;
}

.footnote {
  font-weight: 400;
  font-size: to-rem(14px);
  color: #333333;
}

.missingData {
  filter: blur(10px);
  pointer-events: none;
  opacity: 0.5;
}

.missingDataError {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  max-width: 640px;
}

.error {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  background: var(--white);
  border-radius: var(--border-radius);
  border: 1px solid var(--gray-6);
  padding: 16px 24px;
  text-align: center;
}

.disablePointerEvents {
  pointer-events: none;
}
</style>
